import React, { useContext, useMemo } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Typography,
} from '@material-ui/core';
import { FormProvider } from 'react-hook-form';
import { UseFormReturn } from 'react-hook-form';

import { MAIN_CONTAINER_ID } from '../../../../constants';
import { AppGroupedSelect, AppSelect } from '../../../../components/inputs';
import { AppToggle } from '../../../../components/AppToggle';
import { ControlledAutocomplete } from '../../../../components/controlled-inputs';
import { DeviceContext } from '../../../../contexts';
import { MATCH_MODES, MatchMode, defaultStatusOptions, matchModeOptions } from '../config';
import type { QualificationFilterFormValues, QualificationFilterUserOption } from '../types';

const id = 'qualification-filter-dialog-title';

interface TeamOption {
  name: string;
  value: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
  methods: UseFormReturn<QualificationFilterFormValues>;
  showTeamsSelector: boolean;
  groupOneOptions: TeamOption[];
  groupTwoOptions: TeamOption[];
  qualificationsSelectOptions: string[];
  jobTitleOptions: string[];
  filterUsersSelectOptions: QualificationFilterUserOption[];
  selectedStatusIds: string[];
  onStatusChange: (event: React.ChangeEvent<{ value: unknown }>) => void;
  matchMode: MatchMode;
  onMatchModeChange: (mode: MatchMode) => void;
  menuProps?: { container?: HTMLElement | null };
  dialogContainer?: HTMLElement | null;
}

export const FilterDialog = ({
  isOpen,
  onClose,
  onUpdate,
  methods,
  showTeamsSelector,
  groupOneOptions,
  groupTwoOptions,
  qualificationsSelectOptions,
  jobTitleOptions,
  filterUsersSelectOptions,
  selectedStatusIds,
  onStatusChange,
  matchMode,
  onMatchModeChange,
  menuProps = {},
  dialogContainer,
}: Props) => {
  const { isMobile } = useContext(DeviceContext);
  const isNoneMode = matchMode === MATCH_MODES.hasNone;

  const statusClause = useMemo(
    () =>
      selectedStatusIds
        .map(id => defaultStatusOptions.find(option => option.value === id)?.name)
        .filter(Boolean)
        .join(', '),
    [selectedStatusIds]
  );

  const modeSentence = useMemo(() => {
    if (matchMode === MATCH_MODES.hasAny) {
      return (
        <>
          Show workers who <strong>have any</strong> of the selected qualifications
          {statusClause ? (
            <>
              {' '}
              that are <strong>{statusClause}</strong>
            </>
          ) : null}
          .
        </>
      );
    }
    if (matchMode === MATCH_MODES.hasAll) {
      return (
        <>
          Show workers who <strong>have all</strong> of the selected qualifications
          {statusClause ? (
            <>
              , all <strong>{statusClause}</strong>
            </>
          ) : null}
          .
        </>
      );
    }
    return (
      <>
        Show workers who <strong>have none</strong> of the selected qualifications — the uncarded group.
      </>
    );
  }, [matchMode, statusClause]);

  const handleUpdate = () => {
    onUpdate();
    onClose();
  };

  const dialogContainerEl = dialogContainer ?? document.getElementById(MAIN_CONTAINER_ID);

  return (
    <Dialog
      container={dialogContainerEl}
      fullScreen={isMobile}
      open={isOpen}
      onClose={onClose}
      aria-labelledby={id}
    >
      <FormProvider {...methods}>
        <form style={{ minWidth: '600px' }} onSubmit={e => e.preventDefault()}>
          <DialogTitle id={id}>Filters</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gridGap={16} marginTop={2} minWidth={300}>
              {showTeamsSelector && (
                <Box width="100%" marginBottom={2}>
                  <AppGroupedSelect
                    groupOneOptions={groupOneOptions}
                    groupTwoOptions={groupTwoOptions}
                    name="teamId"
                    label="Team"
                    MenuProps={menuProps}
                  />
                </Box>
              )}

              <ControlledAutocomplete
                name="filterUsers"
                multiple
                isTags
                items={filterUsersSelectOptions}
                label="Team Members"
                getOptionSelected={(a: QualificationFilterUserOption, b: QualificationFilterUserOption) =>
                  a.user_id === b.user_id
                }
              />

              <ControlledAutocomplete
                name="jobTitles"
                multiple
                isTags={true}
                items={jobTitleOptions}
                label="Job titles"
              />

              <ControlledAutocomplete
                name="qualifications"
                multiple
                isTags={true}
                items={qualificationsSelectOptions}
                label="Qualifications"
              />

              <Box>
                <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                  Match mode
                </Typography>
                <Box height={44}>
                  <AppToggle
                    options={matchModeOptions}
                    value={matchMode}
                    onChange={value => onMatchModeChange(value as MatchMode)}
                    primaryActive
                  />
                </Box>
                <Typography variant="caption" color="textSecondary" component="p" style={{ marginTop: 8 }}>
                  {modeSentence}
                </Typography>
              </Box>

              <FormControl fullWidth variant="filled" disabled={isNoneMode}>
                <InputLabel id="qualification-filter-status-label">Status</InputLabel>
                <AppSelect
                  onChange={onStatusChange}
                  value={selectedStatusIds}
                  labelId="qualification-filter-status-label"
                  id="qualification-filter-status"
                  fullWidth
                  multiple
                  displayEmpty
                  disabled={isNoneMode}
                  MenuProps={menuProps}
                  renderValue={(selected: unknown) => {
                    if (Array.isArray(selected)) {
                      return selected
                        .map(
                          (value: string) =>
                            defaultStatusOptions.find(option => option.value === value)?.name
                        )
                        .filter(Boolean)
                        .join(', ');
                    }
                    return (
                      defaultStatusOptions.find(option => option.value === selected)?.name ||
                      String(selected)
                    );
                  }}
                >
                  {defaultStatusOptions.map(item => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.name}
                    </MenuItem>
                  ))}
                </AppSelect>
                {isNoneMode && (
                  <Typography variant="caption" color="textSecondary" style={{ marginTop: 6 }}>
                    There's no qualification to validate when checking for none held.
                  </Typography>
                )}
              </FormControl>
            </Box>
          </DialogContent>

          <DialogActions>
            <Button size="large" variant="outlined" onClick={onClose}>
              Close
            </Button>
            <Button size="large" type="button" variant="contained" color="primary" onClick={handleUpdate}>
              Update
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
